import * as React from 'react';
import { useStore } from 'react-redux';
import { Link } from 'react-router-dom';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { useScrollMemory } from '../../../../hooks/use-scroll-memory';
import { useSearchParams } from '../../../../hooks/use-search-params';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes, ScrollStatePaths } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { FleetActionEditDriver } from '../components/FleetActionEditDriver';
import { DriverStatus } from '../../../../transportation/models/enums/driver-status';
import { DriverSearchFilters } from '../../../../transportation/models/filters/driver-search-filters';
import { DriverRead } from '../../../../transportation/models/fleet/driver-read';
import { getDriverSearchFilters } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps {
    resetFlag: any;
    createFlag: number;
}

export const DriverSetupScene = (props : IProps) => {

    const store         = useStore();
    const [service]     = React.useState(new TransportationSvc());
    const [showInfoFlyout, setShowInfoFlyout] = React.useState(false);
    const [editDriver, setEditDriver] = React.useState(new DriverRead());

    const [filterParams, setFilterParams] = React.useState(() => {
        return getDriverSearchFilters(store.getState());
    });

    const [drivers, setDrivers] = React.useState(() => {
         const initial = new PagedApiResult<DriverRead>();
         initial.pagination = filterParams.paginationInfo;
         initial.pagination.reusePaging = true;
         return initial;
    });

    const scrollTo = useScrollMemory(
        ScrollStatePaths.FLEET_DRIVERS,
        Routes.SHIPPING_FLEET_DRIVERS_EDIT
    );
    
    useSearchParams(
        Routes.SHIPPING_FLEET_DRIVERS_EDIT,
        transportActions.updateDriverSearchFilters(filterParams),
        transportActions.clearDriverSearch()
    );

    React.useEffect(() => {
        if (props.createFlag > 0) {
            setShowInfoFlyout(true);
        }
    }, [props.createFlag]);

    /**
     * OnSearch handler
     * @param filters The driver search filters to apply
     */
    const onSearch = async (filters: DriverSearchFilters) => {
        const response = await service.search(DriverRead, filters);
        if (response.status === StatusCode.Success) {
            setDrivers(response.data);
            scrollTo();
        }

        setFilterParams(filters);
    };

    const onParamsUpdate = (itm : any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        const tmp = new DriverSearchFilters();
        setFilterParams(tmp);
    };

    const onFlyoutClose = async () => {
        props.resetFlag();
        setShowInfoFlyout(false);        
    };

    const onFlyoutSuccess = async (result : number) => {
        props.resetFlag();
        setEditDriver(new DriverRead());
        setShowInfoFlyout(false);

        // For now we will navigate to the newly created pickup.
        // Could be revisited later with a call to search
        // await onSearch(filterParams);
        // onSearch(filterParams);
        navigateToPage(Routes.SHIPPING_FLEET_DRIVERS_EDIT_WITH_ID.formattify(result));
    };

    return (<PageContent classes="fleet-driver-list" pageName={PageNames.TRANSPORTATION_FLEET_DRIVERS}>
                <PageBody>
                    <PagedList
                            noResultsMessage="No drivers found, please search again."
                            searchCallback={ onSearch }
                            data={ drivers }
                            filterProps={ filterParams }
                            selectableClass=""
                            onFilterUpdate={ onParamsUpdate }
                            onReset={ onParamsReset }
                            rowRenderer={(driver: DriverRead) => 
                                <tr key={ driver.id }>
                                    <td>{ driver.employeeId }</td>
                                    <td>
                                        <Link to={Routes.SHIPPING_FLEET_DRIVERS_EDIT_WITH_ID.formattify(driver.id)}>
                                            { driver.firstName } { driver.lastName }
                                        </Link>
                                    </td>
                                    <td>{ AppUtils.formatPhone(driver.cellPhone) }</td>
                                    <td>{ driver.serviceCenterId }</td>
                                    <td>
                                        { 
                                            driver.status === DriverStatus.Active 
                                                ? <span className="driver-status active">ACTIVE</span>
                                                : <span className="driver-status inactive">INACTIVE</span>
                                        }
                                    </td>
                                </tr>
                            }
                        /> 

                    <FleetActionEditDriver
                        visible={ showInfoFlyout }
                        onSuccess={ onFlyoutSuccess }
                        onClose={ onFlyoutClose }
                        driver={ editDriver }
                    /> 
                </PageBody>
            </PageContent>);
    
};
